import './PrivacyPolicy.css';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const useThemeSync = () => {
  useEffect(() => {
    const getTimeBasedTheme = () => {
      const hour = new Date().getHours();
      return (hour >= 19 || hour < 7) ? 'dark' : 'light';
    };
    const saved = localStorage.getItem('munch-theme');
    const savedAt = localStorage.getItem('munch-theme-set-at');
    let theme = getTimeBasedTheme();
    if (saved && savedAt) {
      const hoursSince = (Date.now() - Number(savedAt)) / 3600000;
      if (hoursSince < 12) theme = saved;
    }
    document.documentElement.setAttribute('data-theme', theme);
  }, []);
};

function PrivacyPolicy() {
  useThemeSync();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-page">
      <nav className="privacy-nav">
        <Link to="/" className="privacy-nav-brand">
          <img src={process.env.PUBLIC_URL + '/munch-logo.png'} alt="munch" />
          <span>munch</span>
        </Link>
        <Link to="/" className="privacy-nav-back">
          &larr; back to home
        </Link>
      </nav>

      <main className="privacy-container">
        <div className="privacy-header">
          <h1>privacy policy</h1>
          <p className="privacy-effective">effective date: february 18, 2026</p>
        </div>

        <div className="privacy-content">
          <section className="privacy-section">
            <p>
              Welcome to munch ("we," "us," or "our"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use the munch mobile application (the "App") and our website at trymunch.app (the "Site").
            </p>
            <p>
              By using the App or the Site, you agree to the collection and use of information in accordance with this policy. If you do not agree with this policy, please do not use our services.
            </p>
          </section>

          <section className="privacy-section">
            <h2>1. information we collect</h2>
            <h3>personal information</h3>
            <p>We may collect the following personal information that you voluntarily provide to us:</p>
            <ul>
              <li>Email address (when joining our waitlist or creating an account)</li>
              <li>Name and profile information (when creating an account)</li>
              <li>Dietary preferences and nutrition goals</li>
              <li>Location data (to show nearby restaurants and menu options)</li>
            </ul>

            <h3>automatically collected information</h3>
            <p>When you use our App or visit our Site, we may automatically collect:</p>
            <ul>
              <li>Device information (device type, operating system, unique device identifiers)</li>
              <li>Usage data (features used, pages visited, time spent in the App)</li>
              <li>Log data (IP address, browser type, access times)</li>
              <li>Analytics data to help us improve our services</li>
            </ul>

            <h3>health & fitness data</h3>
            <p>
              If you choose to sync with Apple Health, we may access nutritional and dietary data. This data is used solely to provide you with personalized nutrition tracking and is never sold or shared with third parties.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. how we use your information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve the App and its features</li>
              <li>Personalize your experience, including meal recommendations and nutrition plans</li>
              <li>Show you nearby restaurants and menu items that match your goals</li>
              <li>Send you updates, notifications, and marketing communications (with your consent)</li>
              <li>Analyze usage patterns to improve our services</li>
              <li>Respond to your requests and provide customer support</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
            </ul>
          </section>

          <section className="privacy-section highlight-section">
            <h2>3. we will never sell your data</h2>
            <p>
              <strong>Your personal data will never be sold to third parties without your explicit consent.</strong> We believe your information belongs to you. We do not sell, rent, or trade your personal information to any third-party companies for their marketing purposes — period.
            </p>
            <p>
              We may share anonymized, aggregated data that cannot be used to identify you (such as general usage statistics) to help improve our services, but your individual personal data remains yours.
            </p>
          </section>

          <section className="privacy-section">
            <h2>4. when we may share your information</h2>
            <p>We may share your information only in the following limited circumstances:</p>
            <ul>
              <li><strong>Service providers:</strong> With trusted third-party vendors who help us operate the App (e.g., hosting, analytics, email delivery). These providers are contractually obligated to protect your data and may only use it to perform services on our behalf.</li>
              <li><strong>Legal requirements:</strong> When required by law, regulation, legal process, or governmental request.</li>
              <li><strong>Safety:</strong> To protect the rights, property, or safety of munch, our users, or the public.</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, in which case your data would remain subject to this Privacy Policy.</li>
              <li><strong>With your consent:</strong> When you explicitly authorize us to share your information.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>5. data security</h2>
            <p>
              We implement industry-standard security measures to protect your personal information, including encryption in transit and at rest, secure server infrastructure, and regular security audits. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="privacy-section">
            <h2>6. data retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When you delete your account, we will delete or anonymize your personal data within 30 days, except where we are legally required to retain it.
            </p>
          </section>

          <section className="privacy-section">
            <h2>7. your rights</h2>
            <p>Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data.</li>
              <li><strong>Portability:</strong> Request a portable copy of your data in a commonly used format.</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time.</li>
              <li><strong>Withdraw consent:</strong> Withdraw previously given consent for data processing.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:abehzad.developer@gmail.com">abehzad.developer@gmail.com</a>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8. children's privacy</h2>
            <p>
              Our App is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we discover that we have collected personal information from a child under 13, we will promptly delete that information. If you believe a child under 13 has provided us with personal data, please contact us immediately.
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. third-party links & services</h2>
            <p>
              The App may contain links to third-party websites or services (such as restaurant websites). We are not responsible for the privacy practices of these external sites. We encourage you to review the privacy policies of any third-party services you interact with.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make changes, we will update the "Effective Date" at the top of this page and notify you through the App or via email. Your continued use of the App after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. contact us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="privacy-contact-card">
              <p><strong>munch</strong></p>
              <p>
                Email:{' '}
                <a href="mailto:abehzad.developer@gmail.com">abehzad.developer@gmail.com</a>
              </p>
              <p>
                Website:{' '}
                <a href="https://trymunch.app" target="_blank" rel="noopener noreferrer">trymunch.app</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      <footer className="privacy-footer">
        <p>&copy; 2026 munch. all rights reserved.</p>
        <Link to="/">back to home</Link>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
